import { z } from "zod";
import { isValidCategoryColor } from "@/lib/category-colors";

export const categoryCreateSchema = z.object({
  name: z.string().min(1, "Name is required").max(120),
  /**
   * Never asked for in the form — the API generates a stable internal key
   * from the name on create (`createCategory` in `src/api/categories.ts`)
   * and it is immutable afterward, so this is only ever set server-side.
   */
  code: z.string().trim().min(1).max(20).optional(),
  description: z.string().max(500).nullable().optional(),
  /**
   * A hex value, validated here so a colour the app cannot paint never
   * reaches the database. Null clears it.
   */
  color: z
    .string()
    .refine(isValidCategoryColor, { message: "Must be a colour, e.g. #2563eb" })
    .nullable()
    .optional(),
});

export const categoryUpdateSchema = categoryCreateSchema.partial();

/**
 * Move a category one step up or down in the admin-controlled ordering. The
 * server renumbers `sort_order` around the swap, so the client only ever asks
 * for a direction — never a raw sort_order value.
 */
export const categoryReorderSchema = z.object({
  direction: z.enum(["up", "down"]),
});

/**
 * Skills or competencies to put into, or take out of, one category.
 *
 * A set rather than a single id because the screen lets several be picked
 * before committing.
 */
export const categoryMembershipSchema = z.object({
  ids: z.array(z.string().min(1)).min(1, "Pick at least one"),
});

export type CategoryCreateInput = z.infer<typeof categoryCreateSchema>;
export type CategoryUpdateInput = z.infer<typeof categoryUpdateSchema>;
export type CategoryMembershipInput = z.infer<typeof categoryMembershipSchema>;
